import React from "react";
import type { TempUnit } from "../../services/cookingState";
import type { HomeTemperatureThreshold } from "../../services/homeTemperatureThreshold";

/**
 * Pure presentational editor for a home-temperature comfort range. Shared by
 * `SensorHomeTempCard` (per-sensor) and `BuildingHomeTempModal` (bulk). Stores
 * the value in Celsius internally, but the user types in whichever unit they
 * selected via the F/C toggle. Conversion is whole-degree to match how
 * `formatTemp` rounds elsewhere in the dashboard.
 */
type Props = {
  value: HomeTemperatureThreshold;
  onChange: (next: HomeTemperatureThreshold) => void;
  unit: TempUnit;
  disabled?: boolean;
  /** When true, drop the "Alert when …" checkbox and treat the alert as
   * always enabled. Used by the dashboard's UserConfigEditor where the
   * per-section "tick to change" toggle IS the on/off control -- a second
   * nested checkbox for the same thing is redundant. Default false. */
  hideEnabledToggle?: boolean;
};

// Allowed range in Celsius — generous bracket around any realistic indoor
// temperature. Used to clamp inputs so a fat-finger entry can't push the
// device into nonsense territory.
const MIN_C = -10;
const MAX_C = 50;

function cToUser(c: number, unit: TempUnit): number {
  return unit === "F" ? Math.round((c * 9) / 5 + 32) : Math.round(c);
}

/**
 * Rounded to one decimal, because cToUser rounds and this did not.
 *
 * The asymmetry was invisible in Fahrenheit and lossy in Celsius: 65F stored
 * 18.333333333333332, which round-trips for display in F, but switching to C
 * showed "18" -- and touching that field wrote back exactly 18, a silent 0.33C
 * change to a value the user had not meant to edit. One decimal makes the round
 * trip stable and keeps 17-digit floats off the wire.
 */
function userToC(v: number, unit: TempUnit): number {
  const c = unit === "F" ? ((v - 32) * 5) / 9 : v;
  return Math.round(c * 10) / 10;
}

function clampC(c: number): number {
  if (!Number.isFinite(c)) return MIN_C;
  return Math.max(MIN_C, Math.min(MAX_C, c));
}

/**
 * The narrowest band this editor will produce, in Celsius.
 *
 * The clamps below used to bump the opposite bound ONTO the one being moved, so
 * dragging the minimum past the maximum produced minC === maxC -- a comfort zone
 * with no width, on a feature whose whole job is "tell me when the room is too
 * cold or too hot". Every reading is then either below or above it. The most
 * obvious interaction in the control walked straight into the one bad state it
 * can reach.
 */
const MIN_BAND_C = 1;

/**
 * Whether a band is usable. Exported because BOTH parents need it and one of
 * them was re-deriving it inline while the other did not check at all: the bulk
 * modal had its own `lowC >= highC` copy, and the per-sensor card had no
 * validity check whatsoever, so it would happily save a zero-width band.
 *
 * Deliberately NOT gated on `enabled`. A band stored with min === max while
 * switched off is a trap that springs later -- the user is shown no warning,
 * saves it, enables it another day, and a configuration nobody was warned about
 * goes live.
 *
 * Also deliberately stricter than the API, which only rejects lowC > highC
 * (SanitizeToContract). Equal passes sanitisation and is published to the
 * device. That is the server being permissive about a value this feature cannot
 * use, not a licence for the editor to emit one.
 */
export function isAmbientBandValid(lowC: number, highC: number): boolean {
  return Number.isFinite(lowC) && Number.isFinite(highC) && lowC < highC;
}

export default function HomeTempThresholdEditor({
  value,
  onChange,
  unit,
  disabled,
  hideEnabledToggle,
}: Props) {
  const minDisplay = cToUser(value.minC, unit);
  const maxDisplay = cToUser(value.maxC, unit);

  const setEnabled = (enabled: boolean) => onChange({ ...value, enabled });
  // When the caller owns the on/off via a wrapping control, treat the
  // range as always active for validation + input enable purposes.
  const isEnabled = hideEnabledToggle ? true : value.enabled;

  const setMin = (userValue: number) => {
    const newMinC = clampC(userToC(userValue, unit));
    // Keep a gap rather than bumping the max onto the min: equal bounds are the
    // only invalid state this control can reach, and the old clamp created it.
    const newMaxC = Math.max(value.maxC, newMinC + MIN_BAND_C);
    onChange({ ...value, minC: newMinC, maxC: newMaxC });
  };

  const setMax = (userValue: number) => {
    const newMaxC = clampC(userToC(userValue, unit));
    const newMinC = Math.min(value.minC, newMaxC - MIN_BAND_C);
    onChange({ ...value, minC: newMinC, maxC: newMaxC });
  };

  const invalid = !isAmbientBandValid(value.minC, value.maxC);

  return (
    <div className="flex flex-col gap-4">
      {hideEnabledToggle ? null : (
        <label
          className={`flex items-center gap-3 select-none ${
            disabled ? "opacity-60" : ""
          }`}
        >
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={disabled}
            className="h-4 w-4 accent-[#BBDA62]"
          />
          <span className="text-sm font-medium text-ink">
            Alert when home temperature is outside this range
          </span>
        </label>
      )}

      <div className="grid grid-cols-2 gap-4">
        <ThresholdField
          label="Minimum"
          value={minDisplay}
          unit={unit}
          onChange={setMin}
          disabled={disabled || !isEnabled}
          helper="Below this, the cooktop's sensor will report cold."
        />
        <ThresholdField
          label="Maximum"
          value={maxDisplay}
          unit={unit}
          onChange={setMax}
          disabled={disabled || !isEnabled}
          helper="Above this, the cooktop's sensor will report hot."
        />
      </div>

      {invalid ? (
        <p className="text-alert-critical text-xs">
          The minimum must be lower than the maximum.
        </p>
      ) : null}
    </div>
  );
}

type FieldProps = {
  label: string;
  value: number;
  unit: TempUnit;
  onChange: (v: number) => void;
  disabled?: boolean;
  helper?: string;
};

function ThresholdField({
  label,
  value,
  unit,
  onChange,
  disabled,
  helper,
}: FieldProps) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-ink-3">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={Number.isFinite(value) ? value : ""}
          onChange={(e) => onChange(Number(e.target.value))}
          disabled={disabled}
          className="w-24 rounded-control border border-black/15 bg-white px-3 py-2 text-ink text-base focus:outline-none focus:ring-2 focus:ring-[#BBDA62] disabled:opacity-40"
          inputMode="numeric"
        />
        <span className="text-ink-2 text-sm">°{unit}</span>
      </div>
      {helper ? (
        <span className="text-ink-3 text-xs leading-snug">{helper}</span>
      ) : null}
    </label>
  );
}
